import { useCallback, useEffect, useRef, useState } from "react";
import { providerApi } from "../../lib/api";
import { subscribeRealtimeVersion } from "../../lib/realtime";

const POLL_INTERVAL_MS = 45000;

/**
 * Tracks bookings awaiting provider confirmation for the active venue (or
 * every accessible venue when no venue is selected yet). Polls the existing
 * bookings list endpoint on a fixed cadence and also refreshes when realtime
 * booking events arrive, so the docked notifications panel and nav badge
 * always share one data source — and match the per-venue counts shown by the
 * dashboard and schedule pages.
 */
export function usePendingConfirmations(enabled: boolean, venueId: string | null = null) {
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const venueRef = useRef(venueId);
  venueRef.current = venueId;

  const reload = useCallback(async () => {
    if (!enabled) {
      setPendingCount(0);
      setPendingIds([]);
      return;
    }

    try {
      const response = await providerApi.listBookings({
        statuses: ["pending"],
        ...(venueId ? { venueId } : {}),
      });
      // Only bookings that can still be acted on count as awaiting
      // confirmation: once the booked window has ended, check-in is
      // impossible and the booking is dead weight, not a task. (The
      // dashboard's incoming list already filters the same way.)
      const now = new Date().toISOString();
      const actionable = response.bookings.filter(
        (booking) => booking.status === "pending" && booking.scheduled_end_at > now,
      );
      // Drop stale responses: a slow poll for the previous venue must not
      // overwrite the count after the provider has switched venues.
      if (venueRef.current !== venueId) {
        return;
      }
      setPendingCount(actionable.length);
      setPendingIds(actionable.map((booking) => booking.id));
    } catch {
      // Background poll: keep the last known count if the request fails.
    }
  }, [enabled, venueId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return subscribeRealtimeVersion(() => {
      void reload();
    });
  }, [enabled, reload]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const intervalId = setInterval(() => {
      void reload();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [enabled, reload]);

  return { pendingCount, pendingIds, reload };
}
